import dayjs from 'dayjs'
import { GQLTestDatesQuery, GQLTestPlacesQuery } from '../generated/graphql'
import utc from 'dayjs/plugin/utc.js'
import timezone from 'dayjs/plugin/timezone.js'
dayjs.extend(utc)
dayjs.extend(timezone)

export function constantsFromArray<T extends Array<string>>(arr: T){
	return arr.reduce((obj, key) => ({...obj, [key]: key}), {})
}

export function identifyDate(date: Date | string): number{
	return dayjs(date).tz('Asia/Tokyo').startOf('day').valueOf()
}

type TestDates = GQLTestDatesQuery['testDates']

export function groupByDate<T extends TestDates>(dates: T): {[key: string]: T}{
	return dates.reduce((obj, d) => {
		const id = identifyDate(d.startDate)
		obj[id] ? obj[id].push(d) : obj[id] = [d]
		return obj
	}, {})
}

export function formatDateWithDayOfTheWeek(date: any){
	if(!(date instanceof Date)){
		date = new Date(date)
	}
	if(Number.isNaN(date.getTime())) return ''
	const daysOfTheWeek = ['日', '月', '火', '水', '木', '金', '土']
	return `${formatDate(date)} (${daysOfTheWeek[date.getDay()]})`
}
export function formatDateTimeWithDayOfTheWeek(date: any){
	if(!(date instanceof Date)){
		date = new Date(date)
	}
	if(Number.isNaN(date.getTime())) return ''
	const daysOfTheWeek = ['日', '月', '火', '水', '木', '金', '土']
	return `${formatDate(date)} (${daysOfTheWeek[date.getDay()]}) ${formatTime(date)}`
}

export function formatDate(date: any): string{
	if(!(date instanceof Date)){
		date = new Date(date)
	}
	if(Number.isNaN(date.getTime())) return ''
	const year = date.getFullYear().toString().padStart(4, '0')
	const month = (date.getMonth() + 1).toString().padStart(2, '0')
	const day = date.getDate().toString().padStart(2, '0')
	return `${year}年${month}月${day}日`
}

export function formatTime(date: Date | number | string){
	const d = dayjs(date).tz('Asia/Tokyo')
	return d.isValid() ? d.format('HH:mm') : ''
}

export function formatDateTime(date){
	if(!(date instanceof Date)){
		date = new Date(date)
	}
	if(Number.isNaN(date.getTime())) return ''
	return `${formatDate(date)} ${formatTime(date)}`
}

export const unique = <T>(arr: T[]): T[] => {
	return Array.from(new Set(arr))
}

export function compareGroupedDateEntries<T>(a: [number, T], b: [number, T]): number{
	return Number(a[0]) - Number(b[0])
}

export function byId(value){
	return by('id', value)
}

export function by(key, value){
	return d => d[key] === value
}

export function replaceByItem(arr, item){
	const target = arr.find(byId(item.id))
	if(target){
		const index = arr.indexOf(target)
		return [...arr.slice(0, index), item, ...arr.slice(index + 1)]
	} else {
		return [...arr, item]
	}
}
export function deleteById(arr, id){
	const target = arr.find(byId(id))
	if(target){
		const index = arr.indexOf(target)
		return [...arr.slice(0, index), ...arr.slice(index + 1)]
	} else {
		return [...arr]
	}
}

export function removeNonNumericCharacters(s){
	return toHalfWidth(s).replace(/[^\d]/g, '')
}

export function toHalfWidth(s){
	return s.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
}

export function isValidMailAddress(v){
	return /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/.test(v)
}

export const normalizeCouponCode = (str: string) => {
  return str
    .replace(/[Ａ-Ｚ０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}